// The Output panel: log lines (with %c styling) and tables. Console calls made
// directly from a running script module are copied here too.

const MAX_LINES = 2000;
const MAX_TABLE_ROWS = 250;

function isEntity( value ) {

	return !! value && typeof value === 'object'
		&& typeof value.isA === 'function' && typeof value.id === 'function' && typeof value.get === 'function';

}

function isIfcFile( value ) {

	return !! value && typeof value === 'object'
		&& typeof value.byType === 'function' && typeof value.schema === 'function';

}

function entityLabel( entity ) {

	try {

		let name = null;
		try {

			name = entity.get( 'Name' );

		} catch { /* not every class has a Name */ }

		return `#${ entity.id() }=${ entity.isA() }${ name ? ` "${ name }"` : '' }`;

	} catch {

		return 'IfcEntity';

	}

}

function replacer( _key, value ) {

	if ( typeof value === 'bigint' ) return value.toString();
	if ( isEntity( value ) ) return entityLabel( value );
	if ( isIfcFile( value ) ) return `IfcFile ${ value.schema() }`;
	if ( value instanceof Map ) return Object.fromEntries( value );
	if ( value instanceof Set ) return [ ...value ];
	return value;

}

function format( value ) {

	if ( typeof value === 'string' ) return value;
	if ( value === undefined ) return 'undefined';
	if ( typeof value === 'function' ) return `ƒ ${ value.name || 'anonymous' }()`;
	if ( value instanceof Error ) return value.stack || `${ value.name }: ${ value.message }`;
	if ( isEntity( value ) || isIfcFile( value ) || typeof value === 'bigint' ) return replacer( '', value );
	try {

		const text = JSON.stringify( value, replacer, 2 );
		return text.length > 6000 ? `${ text.slice( 0, 6000 ) }\n…` : text;

	} catch {

		return String( value );

	}

}

/**
 * Output bound to the panel's body element. `trace` holds the blob: URLs of
 * script modules whose console calls are copied to the panel.
 */
export function createOutput( body ) {

	function append( node ) {

		const stick = body.scrollHeight - body.scrollTop - body.clientHeight < 40;
		body.appendChild( node );
		while ( body.childElementCount > MAX_LINES ) body.firstElementChild.remove();
		if ( stick ) body.scrollTop = body.scrollHeight;

	}

	function line( level, args ) {

		const div = document.createElement( 'div' );
		div.className = `pg-line pg-line--${ level }`;
		if ( typeof args[ 0 ] === 'string' && args[ 0 ].includes( '%c' ) ) {

			// console.log( "%c■%c label", "color: red", "" ): each %c styles
			// the text up to the next one, like the devtools console.
			// One wrapper: the line is a flex row.
			const [ first, ...parts ] = args[ 0 ].split( '%c' );
			const rest = args.slice( 1 );
			const text = document.createElement( 'span' );
			text.append( first );
			for ( const part of parts ) {

				const span = document.createElement( 'span' );
				span.style.cssText = String( rest.shift() ?? '' );
				span.textContent = part;
				text.append( span );

			}

			if ( rest.length ) text.append( ` ${ rest.map( format ).join( ' ' ) }` );
			div.append( text );

		} else {

			div.textContent = args.map( format ).join( ' ' );

		}

		append( div );

	}

	function table( data ) {

		const rows = Array.isArray( data )
			? data
			: Object.entries( data ?? {} ).map( ( [ key, value ] ) => ( { '(key)': key, ...( typeof value === 'object' ? value : { value } ) } ) );
		if ( rows.length === 0 ) return line( 'log', [ '(empty table)' ] );

		const columns = [];
		for ( const row of rows.slice( 0, MAX_TABLE_ROWS ) ) {

			const keys = row && typeof row === 'object' && ! isEntity( row ) ? Object.keys( row ) : [ 'value' ];
			for ( const key of keys ) if ( ! columns.includes( key ) ) columns.push( key );

		}

		const tableEl = document.createElement( 'table' );
		tableEl.className = 'pg-table';
		const head = tableEl.createTHead().insertRow();
		for ( const key of columns ) {

			const th = document.createElement( 'th' );
			th.textContent = key;
			head.appendChild( th );

		}

		const tbody = tableEl.createTBody();
		for ( const row of rows.slice( 0, MAX_TABLE_ROWS ) ) {

			const tr = tbody.insertRow();
			const record = row && typeof row === 'object' && ! isEntity( row ) ? row : { value: row };
			for ( const key of columns ) {

				const td = tr.insertCell();
				const value = record[ key ];
				if ( typeof value === 'number' ) td.className = 'num';
				td.textContent = value == null ? '' : format( value );

			}

		}

		const wrap = document.createElement( 'div' );
		wrap.appendChild( tableEl );
		append( wrap );
		if ( rows.length > MAX_TABLE_ROWS ) line( 'sys', [ `${ rows.length - MAX_TABLE_ROWS } more rows not shown` ] );

	}

	// Scripts call the global console. A call made directly from a script
	// module that is still running or listening is copied to the output.
	// Libraries log from inside calls a script makes, so only the immediate
	// caller counts; their logging stays in devtools.
	const trace = new Set();

	function fromScript() {

		if ( trace.size === 0 ) return false;
		// Error, fromScript, the console wrapper, then the caller.
		const caller = String( new Error().stack ?? '' ).split( '\n' )[ 3 ] ?? '';
		for ( const url of trace ) if ( caller.includes( url ) ) return true;
		return false;

	}

	for ( const [ method, level ] of [ [ 'log', 'log' ], [ 'info', 'info' ], [ 'warn', 'warn' ], [ 'error', 'error' ], [ 'debug', 'sys' ], [ 'table', null ] ] ) {

		const native = console[ method ].bind( console );
		console[ method ] = ( ...args ) => {

			native( ...args );
			if ( ! fromScript() ) return;
			if ( level ) line( level, args );
			else table( args[ 0 ] );

		};

	}

	return {
		trace,
		table,
		log: ( ...args ) => line( 'log', args ),
		info: ( ...args ) => line( 'info', args ),
		warn: ( ...args ) => line( 'warn', args ),
		error: ( ...args ) => line( 'error', args ),
		debug: ( ...args ) => line( 'sys', args ),
		clear: () => {

			body.textContent = '';

		},
	};

}
